import clsx from "clsx";
import React, { Suspense } from "react";
import { Toaster } from "react-hot-toast";

import { Icons, LoadingIcon, Tabs } from "@web-app/components";
import { useDocument, useIsDesktop } from "@web-app/hooks";
import useDispatchStore from "@web-app/store/dispatchStore";

import CreateOrderDrawer from "../common/CreateOrderDrawer";
import MapLegend from "../common/MapLegend";
import Navigation from "../common/Navigation";
import OrdersSidebar from "../common/OrdersSidebar";
import TopNavigation from "../common/TopNavigation";

import ChatPanel from "./ChatPanel";
import useDispatchPage from "./useDispatchPage";

const BulkImportModal = React.lazy(() => import("./BulkImportModal"));
const DispatchMap = React.lazy(() => import("../common/DispatchMap"));
const DispatchMapMobile = React.lazy(
  () => import("../common/DispatchMap/DispatchMapMobile"),
);

export default function DispatchPage({ logout }: { logout: () => void }) {
  useDocument({ title: "Dispatch" });

  const {
    user,
    isBulkImportModalOpen,
    setIsBulkImportModalOpen,
    appFilter,
    setAppFilter,
  } = useDispatchPage();

  const { sidebarMode: sidebarModeV2 } = useDispatchStore();
  const [isOrderModalOpen, setIsOrderModalOpen] = React.useState(false);

  const isDesktop = useIsDesktop();

  const isSidebarModeTable = () => {
    return sidebarModeV2 === "table";
  };

  const mobileTabs: {
    content: React.ReactNode;
    icon?: keyof typeof Icons;
    id?: string;
    title: React.ReactNode;
  }[] = [
    {
      content: (
        <div className="h-[calc(100vh)]">
          <OrdersSidebar />
        </div>
      ),
      icon: "listTree",
      id: "orders",
      title: (
        <div className="tw-sidebar-tabs-style">
          <span>List View</span>
        </div>
      ),
    },
    {
      content: (
        <div className="h-[calc(100vh-100px)] rounded-3xl px-3 ">
          <Suspense
            fallback={
              <div className="h-full w-full flex items-center justify-center">
                <LoadingIcon className="w-8 h-8" />
              </div>
            }
          >
            <DispatchMapMobile />
          </Suspense>
        </div>
      ),
      icon: "locationPin",
      id: "map",
      title: (
        <div className="tw-sidebar-tabs-style">
          <span>Map View</span>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen max-w-full">
      <Navigation
        logout={logout}
        setShowCreateOrderForm={setIsOrderModalOpen}
      />
      <Toaster position="top-center" />
      {isDesktop ? (
        <div className="ml-0 lg:ml-20 bg-gray-100 max-h-screen hidden lg:flex  min-h-screen">
          <aside
            className={clsx(
              `inset-y-0 left-20 hidden md:block w-[calc(100%-430px)] xl:w-[calc(100%-560px)] overflow-y-auto border-gray-200 bg-gray-100 px-4 py-4 sm:px-6 lg:px-5`,
              isSidebarModeTable() && "!w-[55%]",
            )}
          >
            <TopNavigation appFilter={appFilter} setAppFilter={setAppFilter} />
            <Suspense
              fallback={
                <div className="h-full w-full flex items-center justify-center">
                  <LoadingIcon className="w-8 h-8" />
                </div>
              }
            >
              <DispatchMap />
            </Suspense>
          </aside>

          {isBulkImportModalOpen && user && (
            <BulkImportModal
              isOpen={isBulkImportModalOpen}
              setIsOpen={setIsBulkImportModalOpen}
              currentWorkspaceName={user.currentWorkspace.name}
              workspaceId={user.currentWorkspace.id}
            />
          )}

          <ChatPanel />

          <main
            className={clsx(
              "w-[430px] xl:w-[560px] bg-white",
              isSidebarModeTable() && "!w-[45%]",
            )}
          >
            <MapLegend />
            <OrdersSidebar />
          </main>
        </div>
      ) : (
        <div className="lg:hidden flex flex-col min-h-dvh pb-safe">
          <main className="w-full bg-white h-screen relative overflow-y-auto">
            <CreateOrderDrawer
              isOpen={isOrderModalOpen}
              onClose={() => setIsOrderModalOpen(false)}
            />
            <Tabs tabs={mobileTabs} unmountTabs={false} tabClassName="w-full" />
          </main>
        </div>
      )}
    </div>
  );
}
